import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  TERMS_LAST_UPDATED_LABEL,
  TERMS_MARKDOWN,
} from "@/lib/termsAndConditions";
import { LegalMarkdown, stripLeadingTitle } from "@/components/legal/LegalMarkdown";

export const TermsAndConditionsDialog = ({
  open,
  onOpenChange,
  onAccept,
  language = "es",
}) => {
  const [confirmed, setConfirmed] = useState(false);

  const handleOpenChange = (nextOpen) => {
    if (!nextOpen) {
      setConfirmed(false);
    }
    onOpenChange(nextOpen);
  };

  const handleAccept = () => {
    if (!confirmed) return;
    onAccept();
    setConfirmed(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="notranslate max-w-3xl gap-0 overflow-hidden p-0"
        closeButtonTestId="signup-terms-close-button"
        data-testid="signup-terms-dialog"
        lang={language}
        translate="no"
      >
        <DialogHeader className="border-b border-border bg-card px-6 pb-4 pt-6">
          <DialogTitle data-testid="signup-terms-dialog-title">
            {language === "es"
              ? "Términos y Condiciones de Voxara"
              : "Voxara Terms and Conditions"}
          </DialogTitle>
          <DialogDescription data-testid="signup-terms-dialog-description">
            {language === "es"
              ? `Última actualización: ${TERMS_LAST_UPDATED_LABEL}`
              : `Last updated: ${TERMS_LAST_UPDATED_LABEL}`}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea
          className="h-[55vh] px-6 py-5"
          data-testid="signup-terms-scroll-area"
        >
          <div className="pr-4" data-testid="signup-terms-content">
            <LegalMarkdown markdown={stripLeadingTitle(TERMS_MARKDOWN)} />
          </div>
        </ScrollArea>

        <div className="space-y-4 border-t border-border bg-muted/20 px-6 py-4">
          <div
            className="flex items-start gap-3 rounded-2xl border border-border bg-background/80 p-4"
            data-testid="signup-terms-confirmation-row"
          >
            <Checkbox
              id="terms-confirmation"
              checked={confirmed}
              onCheckedChange={(checked) => setConfirmed(Boolean(checked))}
              data-testid="signup-terms-checkbox"
            />
            <div className="space-y-1">
              <Label
                htmlFor="terms-confirmation"
                className="cursor-pointer text-sm font-medium leading-5"
              >
                {language === "es"
                  ? "He leído y acepto los Términos y Condiciones de Voxara."
                  : "I have read and accept Voxara’s Terms and Conditions."}
              </Label>
              <p className="text-xs text-muted-foreground">
                {language === "es"
                  ? "Debes aceptar este documento para completar la creación de tu cuenta."
                  : "You must accept this document to complete account creation."}
              </p>
            </div>
          </div>

          <DialogFooter className="gap-3 sm:justify-between sm:space-x-0">
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleOpenChange(false)}
              data-testid="signup-terms-cancel-button"
              translate="no"
            >
              {language === "es" ? "Cerrar" : "Close"}
            </Button>
            <Button
              type="button"
              onClick={handleAccept}
              disabled={!confirmed}
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="signup-terms-accept-button"
              translate="no"
            >
              {language === "es"
                ? "Aceptar y continuar"
                : "Accept and continue"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};